import {
  unifiedSetDoc,
  unifiedUpdateDoc,
  unifiedGetDoc,
  unifiedDeleteDoc,
  unifiedCollection,
  firebaseManager,
} from '@watchlist/shared';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/**
 * Admin-managed home-stack card. Stored in Firestore collection `homeStackCards`.
 * Rendered by HomeCardStack alongside the hardcoded onboarding/feature entries.
 */
export interface HomeStackCard {
  id: string;
  title: string;
  subtitle: string;
  iconType: 'image' | 'svg';
  /** Public URL of the uploaded 100×100 icon. Present when iconType === 'image'. */
  iconImageUrl: string | null;
  /** Raw SVG markup. Present when iconType === 'svg'. */
  iconSvg: string | null;
  /** Where the in-app router should send the user when the card is tapped. */
  navigationPath: string;
  enabled: boolean;
  /** Lower numbers render first; ties broken by createdAt. */
  order: number;
  createdAt: string;
  updatedAt: string;
  createdByUid: string;
}

const COLLECTION = 'homeStackCards';

function nowIso(): string {
  return new Date().toISOString();
}

export async function uploadHomeStackCardIcon(file: File, cardId: string): Promise<string> {
  const storage = await firebaseManager.getStorage();
  const ext = (file.name.split('.').pop() || 'png').replace(/[^a-z0-9]/gi, '').slice(0, 8) || 'png';
  const objectPath = `homeStackCards/${cardId}/icon-${Date.now()}.${ext}`;
  const storageRef = ref(storage, objectPath);
  await uploadBytes(storageRef, file, { contentType: file.type || 'image/png' });
  return getDownloadURL(storageRef);
}

function coerceCard(id: string, data: Record<string, unknown>): HomeStackCard {
  return {
    id,
    title: (data.title as string) ?? '',
    subtitle: (data.subtitle as string) ?? '',
    iconType: ((data.iconType as string) === 'svg' ? 'svg' : 'image') as 'image' | 'svg',
    iconImageUrl: (data.iconImageUrl as string | null) ?? null,
    iconSvg: (data.iconSvg as string | null) ?? null,
    navigationPath: (data.navigationPath as string) ?? '/',
    enabled: (data.enabled as boolean) ?? true,
    order: (data.order as number) ?? 0,
    createdAt: (data.createdAt as string) ?? nowIso(),
    updatedAt: (data.updatedAt as string) ?? nowIso(),
    createdByUid: (data.createdByUid as string) ?? '',
  };
}

export async function createHomeStackCard(
  card: Omit<HomeStackCard, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const id = (crypto.randomUUID?.() ?? `card_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const ts = nowIso();
  await unifiedSetDoc(
    COLLECTION,
    id,
    {
      ...card,
      createdAt: ts,
      updatedAt: ts,
    },
    false,
  );
  return id;
}

export async function updateHomeStackCard(
  id: string,
  partial: Partial<Omit<HomeStackCard, 'id' | 'createdAt' | 'createdByUid'>>,
): Promise<void> {
  await unifiedUpdateDoc(COLLECTION, id, {
    ...partial,
    updatedAt: nowIso(),
  });
}

export async function deleteHomeStackCard(id: string): Promise<void> {
  await unifiedDeleteDoc(COLLECTION, id);
}

export async function getHomeStackCard(id: string): Promise<HomeStackCard | null> {
  const snap = await unifiedGetDoc(COLLECTION, id);
  if (!snap.exists()) return null;
  return coerceCard(id, snap.data() as Record<string, unknown>);
}

export async function listAllHomeStackCards(): Promise<HomeStackCard[]> {
  const docs = await unifiedCollection(COLLECTION).orderBy('order', 'asc').getDocs();
  return docs.map((doc) => coerceCard(doc.id, doc.data() as Record<string, unknown>));
}

export async function listActiveHomeStackCards(): Promise<HomeStackCard[]> {
  const docs = await unifiedCollection(COLLECTION)
    .where('enabled', '==', true)
    .orderBy('order', 'asc')
    .getDocs();
  return docs.map((doc) => coerceCard(doc.id, doc.data() as Record<string, unknown>));
}
